// apps/app/src/components/layout/topbar.tsx
//
// The widget status badge is the most important element in the shell. A clinic
// must never be unsure whether their scanner is running.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Menu, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { http } from '@/lib/api-client';
import { keys } from '@/lib/query-client';
import { useAuth, type TenantState } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { AccountMenu } from './account-menu';
import { CommandPalette } from './command-palette';
import { SidebarNav } from './sidebar';

// `labelKey` is in the common namespace, translated where the badge renders.
const STATUS: Record<TenantState, { labelKey: string; dot: string; tone: string }> = {
  TEST: { labelKey: 'topbar.status.test', dot: 'bg-amber-500', tone: 'border-amber-200 bg-amber-50 text-amber-800' },
  TRIAL: { labelKey: 'topbar.status.live', dot: 'bg-green-500', tone: 'border-green-200 bg-green-50 text-green-800' },
  ACTIVE: { labelKey: 'topbar.status.live', dot: 'bg-green-500', tone: 'border-green-200 bg-green-50 text-green-800' },
  PAST_DUE: { labelKey: 'topbar.status.paymentDue', dot: 'bg-orange-500', tone: 'border-orange-200 bg-orange-50 text-orange-800' },
  DORMANT: { labelKey: 'topbar.status.dormant', dot: 'bg-stone-400', tone: 'border-stone-200 bg-stone-100 text-stone-700' },
  CANCELLED: { labelKey: 'topbar.status.cancelled', dot: 'bg-red-500', tone: 'border-red-200 bg-red-50 text-red-800' },
};

export function Topbar() {
  const user = useAuth((s) => s.user);
  const { t } = useTranslation('common');
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  const { data: tenant } = useQuery({
    queryKey: keys.tenant,
    queryFn: () => http.get<{ state: TenantState; name: string }>('/tenants/me'),
    enabled: Boolean(user?.tenantId),
  });

  // The palette searches one clinic's patients; platform staff have none.
  const hasPalette = Boolean(user?.tenantId);

  useEffect(() => {
    if (!hasPalette) return;
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasPalette]);

  const status = tenant ? STATUS[tenant.state] : null;

  return (
    <header className="z-30 flex h-14 shrink-0 items-center gap-3 border-b bg-card px-4 md:px-6">
      {/* Below lg the rail is hidden; the same navigation opens as a drawer. */}
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setNavOpen(true)} aria-label={t('nav.openNavigation')}>
        <Menu />
      </Button>

      {hasPalette ? (
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className="flex h-9 max-w-md flex-1 items-center gap-2 rounded-md border bg-background px-3 text-left text-sm text-muted-foreground transition-colors duration-fast hover:border-ring/60"
        >
          <Search className="size-4" aria-hidden="true" />
          <span className="flex-1 truncate">{t('topbar.search')}</span>
          <kbd className="hidden rounded border bg-muted px-1.5 py-0.5 font-sans text-[10px] sm:inline">⌘K</kbd>
        </button>
      ) : (
        <Badge className="rounded-md tracking-wide">{t('topbar.platformConsole')}</Badge>
      )}

      <div className="flex-1" />

      {status && (
        <span
          className={cn('flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium', status.tone)}
          // Icon plus label, never colour alone.
          role="status"
        >
          <span className={cn('size-1.5 rounded-full', status.dot)} aria-hidden="true" />
          {t(status.labelKey)}
        </span>
      )}

      {hasPalette && <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />}

      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side="left" className="flex w-72 flex-col border-sidebar-border bg-sidebar p-0 text-sidebar-foreground">
          <SheetHeader className="px-4 pb-2 pt-5 text-left">
            <SheetTitle className="text-[15px] text-white">SkinVizo</SheetTitle>
          </SheetHeader>
          <div className="px-2 pb-3">
            <AccountMenu />
          </div>
          <SidebarNav onNavigate={() => setNavOpen(false)} />
        </SheetContent>
      </Sheet>
    </header>
  );
}
